import { Injectable } from '@angular/core';
import { forkJoin, Observable, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { IBillingData, ICompany, IFallbackMedia, INewPassword, ITimezone, IUser, IUserProfile } from '@interfaces/user';
import { catchError, map, share } from 'rxjs/operators';
import { ErrorService } from '@services/error/error.service';
import { ModalService } from '@services/modal/modal.service';
import { IMetrics } from '@interfaces/metrics';
import { Store } from '@ngxs/store';
import { ToggleGlobalLoader, ToggleMetricsLoader, ToggleSettingsLoader } from '@store/loader';
import { IMediaItem } from '@interfaces/media';
import { UserRole } from '@shared/enums/user-roles.enum';
import * as moment from 'moment-timezone';


@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
    private modal: ModalService,
    private store: Store,
  ) {
  }

  public getUser(): Observable<IUser> {
    const sub$ = this.http.get<IUser>('user/userData').pipe(share());
    sub$.subscribe({error: (err => this.errorService.showError(err))});
    return sub$;
  }

  public getCompanyUsers(): Observable<IUser[]> {
    const sub$ = this.http.get<IUser[]>('company/user').pipe(share());
    sub$.subscribe({error: (err => this.errorService.showError(err))});
    return sub$;
  }

  public addUserToCompany(user: { name: string, pass: string, email: string, role: UserRole }): Observable<IUser> {
    const sub$ = this.http.post<IUser>('company/user', user).pipe(share());
    sub$.subscribe({error: (err => this.errorService.showError(err))});
    return sub$;
  }

  public updateUser(user: {id: number, account_blocked: boolean}): Observable<IUser> {
    const sub$ = this.http.patch<IUser>('company/user', user).pipe(share());
    sub$.subscribe({error: (err => this.errorService.showError(err))});
    return sub$;
  }

  public deleteUser(id: number): Observable<any> {
    const sub$ = this.http.request('delete', 'company/user', {body: {id}}).pipe(share());
    sub$.subscribe({error: (err => this.errorService.showError(err))});
    return sub$;
  }

  public updateUserData({first_name, company, address}: IUserProfile): Observable<[IUser, ICompany]> {
    const sub$ = forkJoin([
      this.http.put<IUser>('user', {name: first_name}),
      this.http.patch<ICompany>('company', {address, company})
    ])
      .pipe(share());

    sub$.subscribe({error: (err => this.errorService.showError(err))});

    return sub$;
  }

  public updateBillingData(data: IBillingData): Observable<ICompany> {
    const sub$ = this.http.patch<ICompany>('company', {
        ...data
      }).pipe(share());

    sub$.subscribe({error: (err => this.errorService.showError(err))});

    return sub$;
  }

  public getCompanyData(): Observable<ICompany> {
    const sub$ = this.http.get<ICompany>('company')
      .pipe(share());

    sub$.subscribe((data) => {
      this.setMarketplaceModeStorage(data.marketplace_mode);
    });
    return sub$;
  }

  public getFeaturesConfig(): Observable<Record<string, boolean>> {
    return this.http.get<Record<string, boolean>>('company/features-config');
  }

  private setMarketplaceModeStorage(status: boolean): void {
    if (!status) localStorage.removeItem('DWallMarketplaceMode');
    else localStorage.setItem('DWallMarketplaceMode', 'true');
  }

  public toggleTwoFA(status: boolean): Observable<any> {
    const sub = this.http
      .patch('company', {two_fa: status})
      .pipe(share());

    sub.subscribe({error: (err => this.errorService.showError(err))});

    return sub;
  }

  public updateIpWhiteList(list: string): Observable<any> {
    const observable = this.http
      .patch('company', {ip_white_list: list}, {responseType: 'text'})
      .pipe(share());

    observable.subscribe({error: (err => this.errorService.showError(err))});
    return observable;
  }

  public updatePass(pass: INewPassword): Observable<any> {
    const observable = this.http
      .put('user/updatePass', pass)
      .pipe(share());

    observable.subscribe(
      ({message}: any) => this.modal.showMsg(message),
      err => this.errorService.showError(err));
    return observable;
  }

  public updateTimezone(tz: ITimezone): Observable<any> {
    this.store.dispatch(new ToggleGlobalLoader(true));
    const observable = this.http
      .post('tv/timezone', {timezone: tz.name})
      .pipe(share());

    observable.subscribe(
      () => {
        this.store.dispatch(new ToggleGlobalLoader(false));
      },
      err => {
        this.store.dispatch(new ToggleGlobalLoader(false));
        this.errorService.showError(err);
      });
    return observable;
  }

  public getDeviceManagementSettings(): Observable<{ status: boolean, corporateId: string }> {
    const observable = this.http.get<{ status: boolean, corporateId: string }>('company/management/status')
      .pipe(share());

    observable.subscribe(
      () => {
      },
      (err) => this.errorService.showError(err));
    return observable;
  }


  public toggleDeviceManagementStatus(status: boolean): Observable<any> {
    const observable = this.http.post(`company/management/${status ? 'activate' : 'disable'}`, {})
      .pipe(share());

    observable.subscribe(
      () => {
      },
      (err) => this.errorService.showError(err));
    return observable;
  }

  public toggleAutoenroll(status: boolean): Observable<any> {
    const observable = this.http.patch(`company/management/autoEnroll`, {status})
      .pipe(share());

    observable.subscribe(
      () => {
      },
      (err) => this.errorService.showError(err));
    return observable;
  }

  public toggleAutoDelete(status: boolean): Observable<any> {
    const observable = this.http.patch(`company/management/autodelete`, {status})
      .pipe(share());

    observable.subscribe(
      () => {
      },
      (err) => this.errorService.showError(err));
    return observable;
  }

  public changeAutoDeletePeriod(period: number): Observable<any> {
    const observable = this.http.patch(`company/management/autodelete/period`, {period})
      .pipe(share());

    observable.subscribe(
      () => {
      },
      (err) => this.errorService.showError(err));
    return observable;
  }

  public toggleStatistic(status: boolean): Observable<{ status: boolean }> {
    const observable = this.http.patch<{ status: boolean }>(`company/management/stat`, {status})
      .pipe(share());

    observable.subscribe(
      () => {
      },
      (err) => this.errorService.showError(err));
    return observable;
  }

  public getTimezones(): ITimezone[] {
    return moment.tz.names().map(name => {
      return {name, offset: moment.tz(name).format('Z, z')} as ITimezone;
    });
  }

  public getCompanyMetrics(): Observable<IMetrics> {
    this.store.dispatch(new ToggleMetricsLoader(true));
    const sub$ = this.http.get<IMetrics>('company/metrics')
      .pipe(
        share(),
        catchError(() => this.store.dispatch(new ToggleMetricsLoader(false)))
      );

    sub$.subscribe(() => this.store.dispatch(new ToggleMetricsLoader(false)));

    return sub$;
  }

  public getFallback(): Observable<[{ status: boolean }, IMediaItem | null]> {
    this.store.dispatch(new ToggleSettingsLoader(true));
    const mediaFb$ = this.http.get<IFallbackMedia | null>(`fallbacks`)
      .pipe(
        share(),
        map((response) => {
          if (!response) return null;
          else return response.media;
        })
      );

    const blackFbStatus$ = this.http.get<{ status: boolean }>(`fallbacks/blackFallback`).pipe(share());

    const sub$ = forkJoin([
      blackFbStatus$,
      mediaFb$,
    ]);

    sub$.pipe(
      catchError(err => {
        this.errorService.showError(err);
        this.store.dispatch(new ToggleSettingsLoader(false));
        return throwError(err);
      })
    );

    sub$.subscribe(() => this.store.dispatch(new ToggleSettingsLoader(false)));

    return sub$;
  }

  public updateFallbackMedia(mediaId: number): Observable<IMediaItem> {
    this.store.dispatch(new ToggleGlobalLoader(true));
    const sub$ = this.http.post<IFallbackMedia>(`fallbacks`, {mediaId})
      .pipe(
        share(),
        map((response) => response.media)
      );

    sub$.subscribe(() => this.store.dispatch(new ToggleGlobalLoader(false)));
    return sub$;
  }

  public setBlackFallback(status: boolean): Observable<IMediaItem | null> {
    this.store.dispatch(new ToggleGlobalLoader(true));
    const sub$ = this.http.post<IFallbackMedia | null>(`fallbacks/blackFallback`, {status})
      .pipe(
        share(),
        map((response) => {
          if (!response) return null;
          else return response.media;
        })
      );

    sub$.subscribe(() => this.store.dispatch(new ToggleGlobalLoader(false)));
    return sub$;
  }

  public setDefaultFallback(): Observable<any> {
    this.store.dispatch(new ToggleGlobalLoader(true));
    const sub$ = this.http.delete(`fallbacks`).pipe(share());

    sub$.subscribe(() => this.store.dispatch(new ToggleGlobalLoader(false)));
    return sub$;
  }

  public setMarketplaceMode(status: boolean): Observable<void> {
    return this.http.patch<void>('company/management/marketplace-mode', {status}).pipe(
      share(),
      catchError(err => {
        this.errorService.showError(err);
        return throwError(err);
      })
    );
  }

  public setAutoRemoteUpdateStatus(status: boolean): Observable<{status: boolean}> {
    return this.http.post<{status: boolean}>('company/management/auto-remote-update', {status})
        .pipe(
            share(),
            catchError(err => {
              this.errorService.showError(err);
              return throwError(err);
            })
        );
  }
}
